import { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api.js';

// Hide this suite itself, and/or individual reports inside it, from the client
// sidebar into a collapsed "Hidden" section — view-only, nothing is deleted and
// every dependent feature (segments, campaigns) keeps working regardless
// (server/navVisibility.js). Same component on both surfaces via `scope`:
// 'admin' (Admin → suite editor) | 'my' (client Settings → Hidden reports).
// Mobile-first: single column, ≥40px tap targets. Hidden state is per-client
// (shared by every login at that client), so this reads/writes the same list
// the sidebar's own hide/unhide icons do — either surface sees the other's edits.
export default function NavHiddenEditor({ suiteId, entityId, scope = 'admin' }) {
  const [suite, setSuite] = useState(null); // /api/my/suites/:id — carries `hidden` on the suite + every report
  const [busy, setBusy] = useState('');

  const load = () => api.mySuite(suiteId).then(setSuite).catch(() => {});
  useEffect(() => { load(); }, [suiteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reports = useMemo(() => {
    if (!suite) return [];
    const out = [];
    for (const set of suite.sets || []) for (const d of set.dashboards || []) out.push(d);
    return out;
  }, [suite]);

  const toggleApi = scope === 'admin' ? api.adminToggleNavHidden : api.toggleMyNavHidden;
  const toggle = async (type, id, hidden) => {
    setBusy(id);
    try { await toggleApi(entityId, type, id, hidden); await load(); } finally { setBusy(''); }
  };

  if (!suite) return <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>;

  return (
    <div>
      <p style={hint}>Tuck an event or a report out of the sidebar into a collapsed "Hidden" section — nothing is deleted, and segments or campaigns built on it keep working exactly as before. Unhide any time to bring it back to its usual place.</p>
      <div style={row}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{suite.name} <span style={{ color: 'var(--muted)' }}>· this event</span></span>
        <button type="button" style={suite.hidden ? unhideBtn : hideBtn} disabled={busy === suiteId} onClick={() => toggle('suite', suiteId, !suite.hidden)}>{suite.hidden ? 'Unhide' : 'Hide'}</button>
      </div>
      {reports.length === 0 ? (
        <div style={empty}>No reports in this event yet.</div>
      ) : reports.map((d) => (
        <div key={d.id} style={row}>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</span>
          <button type="button" style={d.hidden ? unhideBtn : hideBtn} disabled={busy === d.id} onClick={() => toggle('dashboard', d.id, !d.hidden)}>{d.hidden ? 'Unhide' : 'Hide'}</button>
        </div>
      ))}
    </div>
  );
}

const hint = { color: 'var(--muted)', fontSize: 13, margin: '0 0 12px', lineHeight: 1.5 };
const empty = { color: 'var(--muted)', fontSize: 13, padding: '10px 0' };
const row = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', minHeight: 44, border: '1px solid var(--hairline)', borderRadius: 10, fontSize: 13.5, marginBottom: 6, boxSizing: 'border-box' };
const hideBtn = { border: '1.5px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 980, padding: '8px 14px', minHeight: 40, fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 };
const unhideBtn = { ...hideBtn, border: '1.5px solid var(--brand)', color: 'var(--brand)' };
